<script setup lang="ts">
/**
 * Generic read-only highlight field for the raw revision's content segmentation form.
 *
 * One component parameterized by `id`/`value` instead of four near-identical ones — covers
 * `highlights`, `included`, `non_included`, and `important_information`, which all share the
 * same persisted shape (`MixedHighlightValue`) and the same read-only presentation.
 *
 * Dumb, prop-driven wrapper around `PremadeCustomEditor`: the persisted value always arrives
 * via `value` (never sourced from a store), so this stays reusable regardless of which flow
 * loaded the revision snapshot.
 */
import FormSection from "@/components/Document/FormSection/FormSection.vue";
import PremadeCustomEditor from "@/features/experience-shared/highlights-v2/components/PremadeCustomEditor.vue";
import { fromManageable } from "@/features/experience-shared/highlights-v2/utils/highlight-adapter";
import type { MixedHighlightValue } from "@/types/Highlights";

const props = defineProps<{
  /** Field id, e.g. "highlights" | "included" | "non_included" | "important_information". */
  id: string;
  value?: MixedHighlightValue;
  required?: boolean;
}>();

const items = computed(() => fromManageable(props.value ?? { premade: [], custom: [] }));
</script>

<template>
  <FormSection :id="id" :required="required">
    <PremadeCustomEditor :items="items" :data-testid="`revision-raw-${id}`" readonly />
  </FormSection>
</template>
